import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, Clock, MapPin, ChevronRight, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { waLink, PHONE_NUMBER_TEL, PHONE_DISPLAY, ZONES, WA_SUBSCRIBER_MSG } from "@/lib/site";

const LINES = [
  { text: "IL TUO TECNICO", cls: "text-white" },
  { text: "DI QUARTIERE", cls: "text-white" },
  { text: "IN SCOOTER.", cls: "text-white" },
  { text: "ARRIVIAMO IN 90 MINUTI.", cls: "text-[#FF5500]" },
];

const ETAS = {
  "Centro Storico": "25–40 min",
  "Quartieri Spagnoli": "25–40 min",
  Chiaia: "30–45 min",
  Vomero: "35–55 min",
  Posillipo: "40–60 min",
  Fuorigrotta: "40–60 min",
  Toledo: "25–40 min",
  Garibaldi: "30–45 min",
};

export default function Hero() {
  const [zone, setZone] = useState("Centro Storico");
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const msg = `Ciao El Mastro! Ho un'emergenza a Napoli in zona ${zone}. Vi invio il video del guasto.`;

  return (
    <section id="top" className="relative overflow-hidden pt-36 sm:pt-44" data-testid="hero-section">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-24 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 px-4 py-1.5"
              data-testid="hero-eyebrow-badge"
            >
              <MapPin className="h-3.5 w-3.5 text-[#FFB800]" />
              <span className="font-mono-accent text-xs font-bold uppercase tracking-[0.22em] text-[#FFB800]">
                Pronto intervento urbano su due ruote — Napoli
              </span>
            </motion.div>

            <h1
              className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl"
              data-testid="hero-main-heading"
            >
              {LINES.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.cls}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
              data-testid="hero-subtitle"
            >
              Micro-manutenzioni per B&amp;B, Condomìni e Privati a Napoli.
              Prezzi chiari, pagamenti digitali.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <a
                href={waLink(msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta-button"
                onClick={() => toast.success("Apertura WhatsApp in corso — inviaci il video del guasto!")}
                className="group flex min-h-[56px] items-center justify-center gap-3 rounded-xl bg-[#FF5500] px-8 text-lg font-bold text-white shadow-[0_0_40px_rgba(255,85,0,0.35)] transition-all duration-200 hover:scale-[1.03] hover:bg-[#ff6622] active:scale-95"
              >
                <MessageCircle className="h-6 w-6 transition-transform duration-200 group-hover:-rotate-12" strokeWidth={2.5} />
                Richiedi Intervento su WhatsApp
              </a>
              <a
                href={`tel:${PHONE_NUMBER_TEL}`}
                data-testid="hero-phone-cta-button"
                className="flex min-h-[56px] items-center justify-center gap-2 rounded-xl border border-white/15 px-6 font-semibold text-slate-200 transition-colors duration-200 hover:border-[#FFB800]/60 hover:text-[#FFB800]"
              >
                <Phone className="h-5 w-5" />
                {PHONE_DISPLAY}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.95 }}
            >
              <a
                href={waLink(WA_SUBSCRIBER_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-subscriber-cta"
                onClick={() => toast.success("Corsia preferenziale attiva — completa l'indirizzo nel messaggio!")}
                className="group inline-flex min-h-[48px] items-center gap-2 rounded-full border border-[#FFB800]/50 bg-[#FFB800]/10 px-6 text-sm font-bold text-[#FFB800] transition-all duration-200 hover:scale-[1.03] hover:bg-[#FFB800] hover:text-[#060B19] active:scale-95"
              >
                <BadgeCheck className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" strokeWidth={2.5} />
                Sono abbonato — priorità d'uscita
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="mt-8 rounded-xl border border-white/10 bg-[#0D1833] p-4"
              data-testid="hero-eta-calculator"
            >
              <p className="mb-3 font-mono-accent text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                Sei in zona? Controlla l'ETA stimato
              </p>
              <div className="flex flex-wrap gap-2" data-testid="hero-eta-zone-selector">
                {ZONES.map((z) => (
                  <button
                    key={z}
                    data-testid={`hero-zone-${z.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => setZone(z)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                      zone === z
                        ? "border-[#FF5500] bg-[#FF5500] text-white"
                        : "border-white/15 text-slate-300 hover:border-[#FFB800]/60 hover:text-[#FFB800]"
                    }`}
                  >
                    {z}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2" data-testid="hero-eta-display-badge">
                <Clock className="h-4 w-4 text-[#FFB800]" />
                <span className="text-sm text-slate-300">
                  Arrivo stimato a <strong className="text-white">{zone}</strong>:{" "}
                  <strong className="font-mono-accent text-[#FFB800]">{ETAS[zone]}</strong>
                </span>
                <ChevronRight className="h-4 w-4 text-[#FF5500]" />
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5" ref={imgRef}>
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-2xl border border-white/10"
            >
              <motion.img
                src="https://images.pexels.com/photos/34163006/pexels-photo-34163006.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Tecnico El Mastro in scooter tra i vicoli di Napoli"
                data-testid="hero-scooter-image"
                style={{ y }}
                className="h-[420px] w-full scale-110 object-cover sm:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060B19]/90 via-transparent to-[#060B19]/20" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-lg border border-white/10 bg-[#060B19]/80 px-4 py-3 backdrop-blur-md">
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-400">Unità in transito</p>
                  <p className="font-display text-lg font-bold uppercase">MASTRO-03 · Vomero</p>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-500">Foto reale del quartiere</p>
                </div>
                <span className="flex items-center gap-2 rounded-full bg-[#25D366]/15 px-3 py-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping-slow absolute h-full w-full rounded-full bg-[#25D366]" />
                    <span className="relative h-2 w-2 rounded-full bg-[#25D366]" />
                  </span>
                  <span className="font-mono-accent text-xs font-bold text-[#25D366]">EN ROUTE</span>
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <div className="hazard-stripe" />
    </section>
  );
}
